// Club Matches Tab — pure data builders for the standings strip
// (TP_BUILD_BRIEF_CLUB_MATCHES_TAB §0.3, handoff MatchesTabContent.dc.html).
//
// Both builders are PURE: they take the payloads the platform already serves
// (/api/standings/<seasonId>/ rows, /api/cups/<competitionId>/ bracket) plus
// the club's TS team id and return render-ready card data — the tab's
// integration layer feeds them, the components below stay presentational.

import { cupRoundTitle } from "../../pages/competitions/competitionsUtils";

// cups.py serves German round labels (test-pinned, PR #411 F-12) and the
// literal date placeholder "wird ausgelost" for undrawn ties — round titles
// come from the stable keys instead (cupRoundTitle) and the placeholder is
// dropped: the TBD chip already carries "undrawn" (brief: empty renders
// nothing, no German leaking into the EN UI).
const UNDRAWN_DATE = "wird ausgelost";
const cleanDate = (d) => (d && d !== UNDRAWN_DATE ? d : null);

// Points-mode excerpt (brief §0.3, artboard C4 note): club row ±2; a club in
// the top 3 renders rows 1–5. Fewer rows than the window → all rows.
export const buildPointsExcerpt = (standingsRows, clubTeamId) => {
  const rows = Array.isArray(standingsRows) ? standingsRows : [];
  if (!rows.length || !clubTeamId) return null;
  const idx = rows.findIndex((r) => r?.team?.id === clubTeamId);
  if (idx === -1) return null; // club not in this table -> no card
  const clubPos = rows[idx]?.position;
  let start;
  if (clubPos != null && clubPos <= 3) {
    start = 0; // top-3 club -> rows 1–5
  } else {
    start = Math.max(0, idx - 2);
  }
  const windowRows = rows.slice(start, start + 5);
  return windowRows.map((r) => ({
    pos: r.position,
    name: r.team?.short_name || r.team?.name || "",
    logo: r.team?.logo || null,
    teamId: r.team?.id || null,
    pld: r.played,
    gd: (r.goal_diff ?? 0) > 0 ? `+${r.goal_diff}` : String(r.goal_diff ?? 0),
    pts: r.points,
    isClub: r.team?.id === clubTeamId,
  }));
};

const tieHasClub = (tie, clubTeamId) =>
  tie?.home?.id === clubTeamId || tie?.away?.id === clubTeamId;

const tieOpponent = (tie, clubTeamId) =>
  tie?.home?.id === clubTeamId ? tie?.away : tie?.home;

const tieScoreText = (tie, clubTeamId) => {
  const mine = tie?.home?.id === clubTeamId ? tie.home : tie.away;
  const theirs = tieOpponent(tie, clubTeamId);
  if (mine?.score == null || theirs?.score == null) return null;
  return `${mine.score}–${theirs.score}`;
};

const tieWon = (tie, clubTeamId) => {
  if (tie?.winner != null) return tie.winner === clubTeamId;
  const mine = tie?.home?.id === clubTeamId ? tie.home : tie.away;
  const theirs = tieOpponent(tie, clubTeamId);
  const m = mine?.agg_score ?? mine?.score;
  const t = theirs?.agg_score ?? theirs?.score;
  if (m == null || t == null) return null;
  if (m === t) return null; // pens decide upstream; unknown here
  return m > t;
};

// Cup-path card rows (brief §0.3): completed rounds as ✓ lines with score
// (+ agg where present), current/next round as → line with opponent + date,
// TBD chip when undrawn; max 4 rows with "+n earlier rounds" collapse;
// ELIMINATED (a completed club tie was lost) or no club tie at all → null,
// the card is absent.
export const buildCupPath = (bracket, clubTeamId) => {
  const rounds = Array.isArray(bracket?.rounds) ? bracket.rounds : [];
  if (!rounds.length || !clubTeamId) return null;

  const path = [];
  let eliminated = false;
  let sawClub = false;

  rounds.forEach((round) => {
    const ties = Array.isArray(round?.ties) ? round.ties : [];
    const clubTie = ties.find((t) => tieHasClub(t, clubTeamId));
    if (clubTie) {
      sawClub = true;
      const opp = tieOpponent(clubTie, clubTeamId);
      const done =
        clubTie.status === "finished" || clubTie.status === "played" ||
        (clubTie.status !== "tbd" && tieScoreText(clubTie, clubTeamId) != null &&
          clubTie.status !== "upcoming" && clubTie.status !== "scheduled");
      if (done) {
        const won = tieWon(clubTie, clubTeamId);
        if (won === false) eliminated = true;
        const mine = clubTie.home?.id === clubTeamId ? clubTie.home : clubTie.away;
        const agg = mine?.agg_score != null && tieOpponent(clubTie, clubTeamId)?.agg_score != null
          ? `${mine.agg_score}–${tieOpponent(clubTie, clubTeamId).agg_score}`
          : null;
        path.push({
          kind: "done", // renders ✓
          label: cupRoundTitle(round),
          opponent: opp?.short_name || opp?.name || "",
          score: tieScoreText(clubTie, clubTeamId),
          agg,
        });
      } else {
        path.push({
          kind: "next", // renders →
          label: cupRoundTitle(round),
          opponent: opp?.short_name || opp?.name || null,
          date: cleanDate(round.date || clubTie.date),
          tbd: clubTie.status === "tbd" || (!clubTie.home && !clubTie.away),
        });
      }
    } else if (sawClub && !eliminated) {
      // Round after the club's last known tie and the club is still in:
      // undrawn future round -> a single TBD → line (only the next one).
      if (!path.some((p) => p.kind === "next")) {
        path.push({ kind: "next", label: cupRoundTitle(round), opponent: null, date: cleanDate(round.date), tbd: true });
      }
    }
  });

  if (!sawClub || eliminated) return null; // card absent (brief §0.3)

  // Collapse: max 4 rows, oldest completed rounds fold into "+n earlier".
  let collapsedEarlier = 0;
  let rowsOut = path;
  if (path.length > 4) {
    collapsedEarlier = path.length - 4;
    rowsOut = path.slice(collapsedEarlier);
  }
  const currentRound =
    [...path].reverse().find((p) => p.kind === "next")?.label ||
    path[path.length - 1]?.label || "";
  return { rows: rowsOut, earlierCount: collapsedEarlier, currentRound };
};
